import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, XCircle, Send, Unplug, ArrowRight, AlertTriangle, RotateCcw, Power, PlayCircle, StopCircle, PauseCircle, Ban, ShieldCheck, Activity, List, KeyRound, Search, Timer } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '@/components/theme-provider';
import flyioIcon from '../assets/icons/flyio.svg';

const POLL_INTERVAL_OPTIONS = [
  { value: 30, label: '30 seconds' },
  { value: 60, label: '60 seconds' },
  { value: 300, label: '5 minutes' },
  { value: 900, label: '15 minutes' },
];

const formatPollInterval = (seconds?: number): string => {
  if (!seconds) return '60 seconds';
  if (seconds % 60 === 0) {
    const mins = seconds / 60;
    return mins === 1 ? '1 minute' : `${mins} minutes`;
  }
  return `${seconds} second${seconds !== 1 ? 's' : ''}`;
};

const FlyioIntegration: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [exampleApp, setExampleApp] = useState('');
  const [exampleDeveloper, setExampleDeveloper] = useState('');
  const [lastPollAt, setLastPollAt] = useState<string | null>(null);
  const [machinesTracked, setMachinesTracked] = useState(0);
  const [pollInterval, setPollInterval] = useState(60);
  const [connectedOrgSlug, setConnectedOrgSlug] = useState('personal');

  const [apiToken, setApiToken] = useState('');
  const [connectPollInterval, setConnectPollInterval] = useState(60);
  const [orgSlug, setOrgSlug] = useState('personal');

  const fetchStatus = async () => {
    try {
      const data = await apiClient.getFlyioStatus();
      if (data?.connected) {
        setConnected(true);
        setLastPollAt(data.last_poll_at || null);
        setMachinesTracked(data.machines_tracked || 0);
        setPollInterval(data.poll_interval || 60);
        setConnectedOrgSlug(data.org_slug || 'personal');
      } else {
        setConnected(false);
      }
    } catch {
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchStatus();
  }, [user]);

  const handleConnect = async () => {
    setError(null);
    setSuccessMsg(null);
    setSaving(true);
    try {
      await apiClient.connectFlyio({ api_token: apiToken, poll_interval: connectPollInterval, org_slug: orgSlug.trim() || 'personal' });
      setSuccessMsg('Fly.io connected successfully!');
      setApiToken('');
      await fetchStatus();
    } catch (err: any) {
      setError(err.response?.data || err.message || 'Failed to connect');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setError(null);
    setSuccessMsg(null);
    try {
      await apiClient.disconnectFlyio();
      setConnected(false);
      setSuccessMsg('Fly.io disconnected.');
    } catch (err: any) {
      setError(err.response?.data || 'Failed to disconnect');
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const data = await apiClient.testFlyio();
      if (data?.success) {
        setTestResult({ success: true, message: `Connected to Fly.io (${data.app_count ?? 0} app${data.app_count !== 1 ? 's' : ''} accessible)` });
      } else {
        setTestResult({ success: false, message: data?.error || 'Connection test failed' });
      }
    } catch (err: any) {
      setTestResult({ success: false, message: err.response?.data || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {successMsg && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950/30">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700 dark:text-green-400">{successMsg}</AlertDescription>
        </Alert>
      )}

      {connected ? (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={flyioIcon} alt="Fly.io" className="w-7 h-7 object-contain" />
              <div>
                <CardTitle className="flex items-center gap-2">
                  Fly.io
                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Connected</Badge>
                </CardTitle>
                <CardDescription>Machine lifecycle, secrets, and investigation automation</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Machines Tracked</span>
                <p className="font-medium">{machinesTracked.toLocaleString()} machine{machinesTracked !== 1 ? 's' : ''}</p>
              </div>
              {lastPollAt && (
                <div>
                  <span className="text-muted-foreground">Last Poll</span>
                  <p className="font-medium">{new Date(lastPollAt).toLocaleString()}</p>
                </div>
              )}
            </div>

            {testResult && (
              <Alert variant={testResult.success ? 'default' : 'destructive'} className={testResult.success ? 'border-green-500 bg-green-50 dark:bg-green-950/30' : ''}>
                {testResult.success ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4" />}
                <AlertDescription className={testResult.success ? 'text-green-700 dark:text-green-400' : ''}>{testResult.message}</AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} className="gap-1">
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Test Connection
              </Button>
              <Button variant="outline" size="sm" onClick={handleDisconnect} className="gap-1">
                <Unplug className="w-3.5 h-3.5" /> Disconnect
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              Polling Setup
              {lastPollAt ? (
                <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Polling active</Badge>
              ) : (
                <Badge variant="outline" className="text-muted-foreground text-xs font-normal">No polls yet</Badge>
              )}
            </CardTitle>
            <CardDescription>Fly.io does not send outbound webhooks. Kestrel detects Fly machine lifecycle events by polling the Fly Machines API on an interval. Follow these steps to set up polling.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 p-4 rounded-lg grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div className="min-w-0">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">Poll Interval</p>
                <p className="font-medium">{formatPollInterval(pollInterval)}</p>
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">Organization</p>
                <p className="font-medium truncate">{connectedOrgSlug}</p>
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">Machines Tracked</p>
                <p className="font-medium">{machinesTracked.toLocaleString()} machine{machinesTracked !== 1 ? 's' : ''}</p>
              </div>
              {lastPollAt && (
                <div className="min-w-0">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">Last Poll</p>
                  <p className="font-medium">{new Date(lastPollAt).toLocaleString()}</p>
                </div>
              )}
            </div>

            <ol className="space-y-3 text-sm text-muted-foreground">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">1</span>
                <span>Create a Fly.io <strong className="text-foreground">API token</strong> with read access to your organization (<code className="bg-background px-1 rounded">fly tokens create org</code> or from the Fly dashboard) and connect it above.</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">2</span>
                <span>Select the <strong className="text-foreground">Organization</strong> you want Kestrel to monitor so polling covers every app and machine in it.</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">3</span>
                <span>Keep the integration connected. Kestrel polls the <strong className="text-foreground">Fly Machines API</strong> on the interval above and fires the matching <strong className="text-foreground">Fly trigger</strong> when a machine crashes, stops, starts, or an app goes down.</span>
              </li>
            </ol>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Available Workflow Blocks</CardTitle>
            <CardDescription>Use these Fly.io actions and triggers in your workflows to automate machine monitoring and incident response.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Triggers</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Machine Crashed', desc: 'Triggers when a Fly machine crashes or exits with a non-zero status.', Icon: AlertTriangle },
                  { name: 'Machine Stopped', desc: 'Triggers when a machine transitions into a stopped state.', Icon: StopCircle },
                  { name: 'Machine Started', desc: 'Triggers when a machine transitions into a started/running state.', Icon: PlayCircle },
                  { name: 'App Down', desc: 'Triggers when every machine for an app is stopped or unhealthy.', Icon: XCircle },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Restart Machine', desc: 'Restart a Fly machine in place without recreating it.', Icon: RotateCcw },
                  { name: 'Start Machine', desc: 'Start a stopped or suspended machine.', Icon: PlayCircle },
                  { name: 'Stop Machine', desc: 'Gracefully stop a running machine.', Icon: StopCircle },
                  { name: 'Suspend Machine', desc: 'Suspend a machine, preserving memory for fast resume.', Icon: PauseCircle },
                  { name: 'Cordon Machine', desc: 'Cordon a machine so the proxy stops routing traffic to it.', Icon: Ban },
                  { name: 'Uncordon Machine', desc: 'Uncordon a machine so it can receive traffic again.', Icon: ShieldCheck },
                  { name: 'Get Machine', desc: 'Retrieve a machine\u2019s state, config, and region.', Icon: Power },
                  { name: 'Get Machine Events', desc: 'Fetch the recent event history for a machine to diagnose issues.', Icon: Activity },
                  { name: 'List Machines', desc: 'List machines for an app, optionally filtered by state.', Icon: List },
                  { name: 'Set Secrets', desc: 'Create or update app secrets and stage them for deploy.', Icon: KeyRound },
                  { name: 'Investigate Fly', desc: 'AI investigates crashes, restarts, and configuration issues.', Icon: Search },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Example Workflow</CardTitle>
            <CardDescription>Click the highlighted fields below to customize, then create the workflow.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground leading-loose">
              When a Fly machine crashes for the{' '}
              <input
                value={exampleApp}
                onChange={e => setExampleApp(e.target.value)}
                placeholder="app"
                style={{ width: `${Math.max(exampleApp.length || 3, 3)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}
              app, get the machine events, investigate the root cause, DM the{' '}
              <input
                value={exampleDeveloper}
                onChange={e => setExampleDeveloper(e.target.value)}
                placeholder="developer"
                style={{ width: `${Math.max(exampleDeveloper.length || 9, 9)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}
              on Slack with the summary, and create a Jira bug ticket.
            </div>
            <Button
              size="sm"
              className="gap-1.5"
              disabled={!exampleApp || !exampleDeveloper}
              onClick={() => navigate('/workflows/new', {
                state: {
                  suggestion: {
                    nl_prompt: `When a Fly machine crashes for the ${exampleApp} app, get the machine events, investigate the root cause, DM ${exampleDeveloper} on Slack with the summary, and create a Jira bug ticket.`,
                  },
                },
              })}
            >
              Create This Workflow <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </CardContent>
        </Card>
        </>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={flyioIcon} alt="Fly.io" className="w-7 h-7 object-contain" />
              <div>
                <CardTitle>Connect Fly.io</CardTitle>
                <CardDescription>Connect your Fly.io account to enable machine lifecycle monitoring, secrets, and investigation automation in workflows.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="fly-token" className="text-sm">API Token</Label>
              <Input
                id="fly-token"
                type="password"
                value={apiToken}
                onChange={e => setApiToken(e.target.value)}
                placeholder="Your Fly.io API token"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Create a token with <code className="bg-background px-1 rounded">fly tokens create org</code> or a deploy token. It is sent as a Bearer token to <strong className="text-foreground">https://api.machines.dev</strong> to read machine state and perform machine actions across your apps.
              </p>
            </div>
            <div>
              <Label htmlFor="fly-poll" className="text-sm">Poll Interval</Label>
              <select
                id="fly-poll"
                value={connectPollInterval}
                onChange={e => setConnectPollInterval(Number(e.target.value))}
                className="mt-1 w-full sm:w-auto block rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {POLL_INTERVAL_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Unlike most integrations, Fly.io doesn't send outbound webhooks, so Kestrel polls the Fly Machines API to detect machine lifecycle events. Faster intervals detect incidents sooner; the minimum is 30 seconds.
              </p>
            </div>
            <div>
              <Label htmlFor="fly-org" className="text-sm">Organization Slug</Label>
              <Input
                id="fly-org"
                type="text"
                value={orgSlug}
                onChange={e => setOrgSlug(e.target.value)}
                placeholder="personal"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                The Fly.io organization whose apps Kestrel should manage. Defaults to <code className="bg-background px-1 rounded">personal</code>. Find it with <code className="bg-background px-1 rounded">fly orgs list</code> — use the slug, not the display name.
              </p>
            </div>
            <Button onClick={handleConnect} disabled={saving || !apiToken}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Connect Fly.io
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FlyioIntegration;
